// Notice periods: every "N days' notice" in the contract, and what each one is for. The contract's
// notice period is the one that matters before it ends (notice of non-renewal, or to terminate);
// a period to cure a breach, a probation-only notice or a payment term isn't it.
import type { Sentence } from "./text";
import { DURATION, QTY, quantity, toDays, unitOf } from "./numbers";

export type NoticeWho = "either" | "tenant" | "landlord" | "employee" | "employer" | "customer" | "provider" | "other";

export interface Notice {
  days: number;
  /** 4 = before the end or renewal, 3 = to terminate or cancel, 1 = something else. */
  strength: number;
  who: NoticeWho;
  /** To end the contract without a reason (for convenience). */
  convenience: boolean;
  /** Only while on probation. */
  probation: boolean;
  sentence: string;
  index: number;
  length: number;
}

const NOTICE_PATTERNS = [
  // "thirty (30) days' prior written notice", "one month's notice", "two months notice", "not less than six months' written notice"
  new RegExp(`${DURATION}\\s+(?:(?:prior|advance|written|advanced|clear|formal|express)\\s+)*(?:written\\s+)?notice`, "giu"),
  // "notice of at least 30 days", "notice period shall be sixty (60) days", "Notice Period: 30 days"
  new RegExp(`notice(?:\\s+period)?(?:\\s+(?:of|shall\\s+be|will\\s+be|is)|\\s*[:\\-–])\\s+(?:at\\s+least\\s+|not\\s+less\\s+than\\s+|a\\s+minimum\\s+of\\s+|minimum\\s+)?${DURATION}`, "giu"),
  // "written notice … at least ninety (90) days before the end of the then-current term"
  new RegExp(`(?:notice|notif(?:y|ies|ied|ication))\\b[^.;]{0,140}?(?:at\\s+least\\s+|not\\s+less\\s+than\\s+|no\\s+later\\s+than\\s+|minimum\\s+)?${DURATION}\\s+(?:before|prior\\s+to|in\\s+advance\\s+of)\\s+(?:the\\s+)?(?:end|expir|expiry|renewal|close|last\\s+day)`, "giu"),
];

const CURE = /\b(?:cure[sd]?|remed(?:y|ied|ies)|rectif(?:y|ied)|make\s+good|breach\w*\s+(?:is\s+)?not)\b/i;
const BEFORE_END = /\b(?:non[\s-]?renewal|not\s+(?:to\s+)?renew|intent(?:ion)?\s+not\s+to\s+renew|before\s+(?:the\s+)?(?:end|expir\w*)|prior\s+to\s+(?:the\s+)?(?:end|expir\w*)|option\s+to\s+renew|renew\w*\s+(?:the|this)\s+agreement)\b/i;
const TERMINATE = /\b(?:terminat\w*|cancel\w*|vacat\w*|end\s+(?:this|the)\s+(?:agreement|contract|lease|licen[cs]e|employment|membership|mou)|resign\w*|quit|withdraw|discontinu\w*|determin\w*\s+(?:this|the)\s+(?:lease|tenancy))\b/i;
const CONVENIENCE = /\b(?:for\s+convenience|without\s+(?:any\s+)?(?:cause|reason)|for\s+any\s+reason|at\s+any\s+time|(?:either|any)\s+party\s+(?:may|can|shall\s+be\s+entitled\s+to)|(?:may|can)\s+(?:terminate|cancel|end)\s+(?:this|the)\s+\w+\s+by\s+giving)\b/i;
const NOT_NOTICE = /\b(?:invoice|payment|pay\s+(?:each|the|any)|interest|refund\w*|return\s+or\s+destroy|free[\s-]look|grace\s+period|claim\w*|deliver\w*)\b/i;

const WHO: [NoticeWho, RegExp][] = [
  ["either", /\b(?:either|each|both|any)\s+(?:part(?:y|ies)|sides?)\b|\beither\s+of\s+(?:us|the\s+parties)\b/i],
  ["tenant", /\b(?:the\s+)?(?:lessee|tenant|licensee)s?\b/i],
  ["landlord", /\b(?:the\s+)?(?:lessor|landlord|licensor|owner)s?\b/i],
  ["employee", /\b(?:the\s+)?employee\b|\bresign\w*|\byou\b/i],
  ["employer", /\b(?:the\s+)?(?:employer|company)\b/i],
  ["customer", /\b(?:the\s+)?(?:customer|client|member|subscriber|buyer|franchisee|licensee)\b/i],
  ["provider", /\b(?:the\s+)?(?:provider|supplier|vendor|contractor|consultant|developer|franchisor|service\s+provider)\b/i],
];

const SUBJECT = /\b((?:either|each|both|any)\s+(?:part(?:y|ies)|sides?)|(?:the\s+)?\p{L}[\p{L}-]+)\s+(?:may|can|shall|will|agrees?\s+to|must|is\s+entitled\s+to|has\s+the\s+right)\b/giu;

/** Who gives the notice: the last "X may / shall / agrees to" before it, or whoever the sentence names first. */
function whoGives(sentence: string, at: number): NoticeWho {
  const lead = sentence.slice(0, at);
  let subject: string | undefined;
  for (const m of lead.matchAll(SUBJECT)) subject = m[1];
  for (const [who, re] of WHO) if (subject && re.test(subject)) return who;
  for (const [who, re] of WHO) if (re.test(lead)) return who;
  return "other";
}

export function findNotices(sents: readonly Sentence[]): Notice[] {
  const out: Notice[] = [];
  for (const s of sents) {
    for (const re of NOTICE_PATTERNS) {
      for (const m of s.text.matchAll(re)) {
        const n = quantity(m[1]!);
        if (n === null || n < 0) continue;
        const days = Math.round(toDays(n, unitOf(m[2]!)));
        if (days > 3650 || out.some((o) => o.index === s.index + m.index!)) continue;
        // The part of the sentence about this notice (up to the next one).
        const around = s.text.slice(Math.max(0, m.index! - 160), m.index! + m[0].length + 60);
        const cure = CURE.test(s.text.slice(Math.max(0, m.index! - 120), m.index! + m[0].length + 40));
        let strength = /^notice\s+period/i.test(m[0]) ? 3 : 1;
        if (BEFORE_END.test(around)) strength = 4;
        else if (TERMINATE.test(around) && !cure) strength = 3;
        else if (strength < 3 && (NOT_NOTICE.test(around) || cure)) strength = 0;
        const probation = /\bprobation\b/i.test(around) && !/\b(?:after|post|following|upon|on)\s+(?:the\s+)?confirmation\b|\bconfirm(?:ed|ation)\b[^.]*$/i.test(s.text.slice(0, m.index!));
        out.push({
          days,
          strength,
          who: whoGives(s.text, m.index!),
          convenience: strength >= 3 && !cure && CONVENIENCE.test(around) && !/\bbreach|default|insolven\w*|for\s+cause\b/i.test(around),
          probation,
          sentence: s.text,
          index: s.index + m.index!,
          length: m[0].length,
        });
      }
    }
  }
  return out.sort((a, b) => a.index - b.index);
}

/**
 * The notice period that matters before the contract ends: before-the-end notices first, then
 * termination, then a notice the contract doesn't say the purpose of. Probation-only notices come
 * last; cure periods and payment terms never count.
 */
export function mainNotice(notices: readonly Notice[]): Notice | null {
  const ranked = notices.filter((n) => n.strength >= 1).sort((a, b) => Number(a.probation) - Number(b.probation) || b.strength - a.strength || a.index - b.index);
  return ranked[0] ?? null;
}

// Re-exported for the terms extractor.
export { QTY };
